import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Table2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import WidgetCard from '../components/WidgetCard';
import { columnLabel, formatFigure } from '../lib/model';
import type { ModelGrid } from '../lib/types';
import type { WidgetProps } from './types';

interface FiguresConfig {
  title?: string;
  concepts?: string[];
  grain?: 'month' | 'quarter' | 'year';
  periods?: number;
}

// Lines of the model over recent periods, exactly as the Model sheet has them.
export default function Figures({ config }: WidgetProps<FiguresConfig>) {
  const { authFetch } = useAuth();
  const [grid, setGrid] = useState<ModelGrid | null>(null);
  const grain = config.grain ?? 'month';
  const periods = config.periods ?? 6;

  useEffect(() => {
    authFetch(`/knowledge/model?grain=${grain}&periods=${periods}`)
      .then(r => r.ok ? r.json() : null)
      .then(setGrid)
      .catch(() => setGrid(null));
  }, [authFetch, grain, periods]);

  if (!grid) return null;
  const wanted = config.concepts ?? [];
  const rows = wanted.length
    ? wanted.flatMap(c => grid.rows.filter(r => r.concept === c))
    : grid.rows.filter(r => r.depth === 0);
  const shown = rows.filter(r => r.cells.some(Boolean));
  if (shown.length === 0) return null;

  return (
    <WidgetCard
      title={config.title || 'Figures'}
      icon={Table2}
      action={<Link to="/model" className="text-[12px] text-brand-dark hover:underline">Open the model</Link>}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-coolslate">
              <th className="text-left font-medium py-1.5 pr-4" />
              {grid.columns.map(col => (
                <th key={col.end} className="text-right font-medium py-1.5 px-2 whitespace-nowrap">{columnLabel(col)}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-softgrey/60">
            {shown.map(row => (
              <tr key={row.concept}>
                <td className="py-1.5 pr-4 text-graphite whitespace-nowrap">{row.name}</td>
                {row.cells.map((cell, i) => (
                  <td key={i} className="py-1.5 px-2 text-right tabular-nums text-graphite whitespace-nowrap">
                    {formatFigure(cell, row.unit)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </WidgetCard>
  );
}
